import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Title } from 'src/design/atoms/Title';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { Select } from 'src/design/atoms/Select';
import { TextInput } from 'src/design/atoms/TextInput';
import { Textarea } from 'src/design/atoms/Textarea';
import { useFormik } from 'formik';
import moment from 'moment';
import React from 'react';
import { WbsAdjustmentRequestDto } from 'src/api/FiretrolApiClient';
import { getErrorMessage, logger, showSuccessToast } from 'src/utils';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { SaveButton } from 'src/design/atoms/SaveButton';
import * as Yup from 'yup';
import { useWbs } from 'src/routes/Jobs/WBS/contexts/WbsContext';
import { WbsAdjustmentTypeSelector } from './WbsAdjustmentTypeSelector';

interface EditAdjustmentFormProps {
  onSave: () => void;
  onCancel: () => void;
}

const validationSchema = Yup.object({
  adjustmentTypeId: Yup.string().required('Adjustment type is required'),
  amount: Yup.number().required('Amount is required'),
  description: Yup.string().nullable(),
  adjustmentDate: Yup.date().required('Adjustment date is required'),
  target: Yup.string()
    .oneOf(['quantity', 'hours'])
    .required('Target is required'),
});

export const EditAdjustmentForm: React.FC<EditAdjustmentFormProps> = ({
  onSave,
  onCancel,
}) => {
  const {
    selectedTreeRow,
    selectedAdjustment,
    createAdjustment,
    updateAdjustment,
  } = useWbs();
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  const isLaborAccount = selectedTreeRow?.accountType === 'labor';
  const initialTarget = isLaborAccount
    ? selectedAdjustment?.target || 'quantity'
    : 'quantity';
  const formik = useFormik({
    initialValues: {
      adjustmentTypeId: selectedAdjustment?.adjustmentTypeId || '',
      amount: selectedAdjustment?.amount || 0,
      description: selectedAdjustment?.description || '',
      adjustmentDate: selectedAdjustment?.adjustmentDate
        ? moment(selectedAdjustment.adjustmentDate).format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD'),
      target: initialTarget,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!selectedTreeRow || selectedTreeRow.isArtificial) return;
      setLoading(true);
      setError(null);
      try {
        if (selectedAdjustment) {
          const payload: WbsAdjustmentRequestDto = {
            adjustmentTypeId: values.adjustmentTypeId,
            amount: values.amount,
            description: values.description || undefined,
            adjustmentDate: moment(values.adjustmentDate).toDate(),
            target: isLaborAccount ? values.target : 'quantity',
          };
          await updateAdjustment(selectedAdjustment.id, payload);
          showSuccessToast('Adjustment updated successfully!');
        } else {
          const payload: WbsAdjustmentRequestDto = {
            adjustmentTypeId: values.adjustmentTypeId,
            amount: values.amount,
            description: values.description || undefined,
            adjustmentDate: moment(values.adjustmentDate).toDate(),
            target: isLaborAccount ? values.target : 'quantity',
          };
          await createAdjustment(payload);
          showSuccessToast('Adjustment created successfully!');
        }
        onSave();
        formik.resetForm();
      } catch (err) {
        logger.error('Failed to save adjustment', { err });
        setError(getErrorMessage(err, 'Unknown error occurred'));
      } finally {
        setLoading(false);
      }
    },
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      formik.handleSubmit();
    }
  };

  if (!selectedTreeRow) {
    return null;
  }

  if (selectedTreeRow.isArtificial) {
    return (
      <Alert color="blue" title="Information">
        Adjustments can only be created for charge codes, not roll-up accounts.
      </Alert>
    );
  }

  // Check if trying to edit a system-generated adjustment
  if (selectedAdjustment && selectedAdjustment.isSystemGenerated) {
    return (
      <Alert color="blue" title="System Generated Adjustment">
        This adjustment was automatically generated by the system and cannot be
        edited manually.
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Title order={5}>
        {selectedAdjustment ? 'Edit Adjustment' : 'Create New Adjustment'}
      </Title>

      <Stack gap="sm">
        <WbsAdjustmentTypeSelector
          value={formik.values.adjustmentTypeId}
          onChange={(value) =>
            formik.setFieldValue('adjustmentTypeId', value || '')
          }
          onBlur={formik.handleBlur}
          error={
            formik.touched.adjustmentTypeId && formik.errors.adjustmentTypeId
          }
          required
          onKeyDown={handleKeyDown}
        />
        {isLaborAccount && (
          <Select
            label="Target"
            data={[
              { value: 'quantity', label: 'Quantity' },
              { value: 'hours', label: 'Hours' },
            ]}
            value={formik.values.target}
            onChange={(value) => formik.setFieldValue('target', value || '')}
            onBlur={formik.handleBlur}
            error={formik.touched.target && formik.errors.target}
            required
            data-autocomplete="off"
          />
        )}

        <NumberInput
          label="Amount"
          placeholder="Enter amount"
          value={formik.values.amount}
          onChange={(value) => formik.setFieldValue('amount', value || 0)}
          onBlur={formik.handleBlur}
          error={formik.touched.amount && formik.errors.amount}
          decimalScale={2}
          required
          onKeyDown={handleKeyDown}
        />

        <TextInput
          label="Adjustment Date"
          type="date"
          value={formik.values.adjustmentDate}
          onChange={(e) =>
            formik.setFieldValue('adjustmentDate', e.target.value)
          }
          onBlur={formik.handleBlur}
          error={formik.touched.adjustmentDate && formik.errors.adjustmentDate}
          required
          onKeyDown={handleKeyDown}
        />

        <Textarea
          label="Description"
          placeholder="Enter adjustment description (optional)"
          value={formik.values.description}
          onChange={(e) => formik.setFieldValue('description', e.target.value)}
          onBlur={formik.handleBlur}
          error={formik.touched.description && formik.errors.description}
          autosize
          minRows={2}
          onKeyDown={handleKeyDown}
        />
      </Stack>

      {error && (
        <Alert color="red" title="Error">
          {error}
        </Alert>
      )}

      <Group justify="flex-end" mt="md">
        <BasicButton
          color="black"
          variant="light"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </BasicButton>
        <SaveButton
          onClick={() => formik.handleSubmit()}
          disabled={!formik.isValid || !formik.dirty}
          loading={loading}
        >
          {selectedAdjustment ? 'Update' : 'Create'} Adjustment
        </SaveButton>
      </Group>
    </Stack>
  );
};
